import React, { useEffect, useState } from 'react';
import { FaCheck } from 'react-icons/fa';
import ProductImage from '../shared/ProductImage';
import ProductNameSearchModal from '../shared/ProductNameSearchModal';
import { getVariantWeight } from '../../services/SKUListAPI';
import { renderSkuWithSoftBreaks } from '../../utils/skuSoftBreak';
import { formatLbsOz } from '@/shared/utils/weightOz';
import type { InventoryTransferLineEvent, InventoryTransferPickLine } from '../../schemas/apiResponse.schemas';
import { transferChangeHistory } from './transferChangeHistory';
import { normalizeSku } from '../../utils/normalizeSku';
import type { SkuNameItem } from '../../services/SKUListAPI';
import '../labels/PrintBoxLabel.css';
import '../shared/ProductInfoModal.css';
import './InventoryTransferChangeModal.css';

interface InventoryTransferChangeModalProps {
  readonly line: InventoryTransferPickLine;
  readonly lines: readonly InventoryTransferPickLine[];
  readonly events: readonly InventoryTransferLineEvent[];
  readonly saving: boolean;
  readonly error: string | null;
  readonly onClose: () => void;
  readonly onChangeQuantity: (quantity: number) => Promise<void>;
  readonly onSubstitute: (sku: string, note?: string) => Promise<void>;
  readonly printing: boolean;
  readonly printError: string | null;
  readonly onPrintStoreLabels: () => Promise<void>;
}

function formatEventWhen(isoString: string): string {
  const parsed = new Date(isoString);
  if (Number.isNaN(parsed.getTime())) return isoString;
  const month = parsed.getMonth() + 1;
  const day = parsed.getDate();
  const year = String(parsed.getFullYear() % 100).padStart(2, '0');
  const hour = parsed.getHours() % 12 || 12;
  const minute = String(parsed.getMinutes()).padStart(2, '0');
  return `${month}/${day}/${year} ${hour}:${minute}`;
}

function parseQuantity(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return Number.isInteger(parsed) ? parsed : null;
}

function InventoryTransferChangeModal({
  line,
  lines,
  events,
  saving,
  error,
  onClose,
  onChangeQuantity,
  onSubstitute,
  printing,
  printError,
  onPrintStoreLabels,
}: InventoryTransferChangeModalProps): React.ReactElement {
  const history = transferChangeHistory(line.id, line.sku, events);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(line.quantity));
  const [weightOz, setWeightOz] = useState<number | null>(null);
  const [weightLoaded, setWeightLoaded] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [pendingSku, setPendingSku] = useState<SkuNameItem | null>(null);
  const [noteEditorOpen, setNoteEditorOpen] = useState(false);
  const [noteInput, setNoteInput] = useState('');
  const [noteText, setNoteText] = useState('');

  useEffect(() => {
    setDraft(String(line.quantity));
    setEditing(false);
  }, [line.id, line.quantity, line.sku]);

  useEffect(() => {
    setNoteEditorOpen(false);
    setNoteInput('');
    setNoteText('');
  }, [pendingSku?.sku]);

  useEffect(() => {
    let cancelled = false;
    setWeightLoaded(false);
    setWeightOz(null);
    void (async (): Promise<void> => {
      const variant = await getVariantWeight(line.sku);
      if (cancelled) return;
      setWeightOz(variant?.weightOz ?? null);
      setWeightLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [line.sku]);

  const existingLine = pendingSku
    ? lines.find((other) => (
      other.id !== line.id && normalizeSku(other.sku) === normalizeSku(pendingSku.sku)
    ))
    : undefined;
  const parsedQuantity = parseQuantity(draft);
  const canSubmitQuantity = parsedQuantity != null && parsedQuantity !== line.quantity && !saving;
  const displayName = line.productName || line.sku;

  const submitQuantity = (): void => {
    if (!canSubmitQuantity || parsedQuantity == null) return;
    void onChangeQuantity(parsedQuantity).catch(() => undefined);
  };

  const confirmSubstitute = (): void => {
    if (!pendingSku || saving) return;
    const note = noteText.trim();
    void (async (): Promise<void> => {
      try {
        await onSubstitute(pendingSku.sku, note === '' ? undefined : note);
        setPendingSku(null);
        setSearchOpen(false);
      } catch {
        // The parent stores the error and leaves this search open.
      }
    })();
  };

  const writeNote = (): void => {
    const trimmed = noteInput.trim();
    if (!trimmed) return;
    setNoteText(trimmed);
    setNoteEditorOpen(false);
  };

  return (
    <div className="pbl-modal-overlay" onClick={onClose}>
      <div className="pbl-modal product-info-modal" onClick={(event) => event.stopPropagation()}>
        <button type="button" className="pbl-modal-close" onClick={onClose} aria-label="Close">×</button>
        <div className="product-info-content">
          <h2 className="product-info-sku">{renderSkuWithSoftBreaks(line.sku)}</h2>
          <ProductImage sku={line.sku} alt={displayName} />
          <div className="product-info-name">{displayName}</div>
          {weightLoaded ? (
            <div className="product-info-weight">
              {weightOz != null ? `Weight ${formatLbsOz(weightOz)}` : 'No weight'}
            </div>
          ) : null}

          <div className="inventory-transfer-qty">
            <div className="inventory-transfer-qty-split">
              <div className="inventory-transfer-qty-field">
                <div className="inventory-transfer-qty-label">Requested Qty</div>
                <div className="inventory-transfer-qty-box">
                  <div className="inventory-transfer-qty-value inventory-transfer-qty-static" aria-label="Requested quantity">
                    {line.requestedQuantity}
                  </div>
                </div>
              </div>
              <span className="inventory-transfer-qty-arrow" aria-hidden="true">→</span>
              <div className="inventory-transfer-qty-field">
                <div className="inventory-transfer-qty-label">Fulfill Qty</div>
                <div className="inventory-transfer-qty-box">
                  {editing ? (
                    <input
                      className="inventory-transfer-qty-input"
                      inputMode="numeric"
                      value={draft}
                      autoFocus
                      aria-label="Fulfill quantity"
                      onChange={(event) => setDraft(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          submitQuantity();
                        }
                      }}
                    />
                  ) : (
                    <button
                      type="button"
                      className="inventory-transfer-qty-value"
                      onClick={() => setEditing(true)}
                    >
                      {line.quantity}
                    </button>
                  )}
                  <button
                    type="button"
                    className="inventory-transfer-qty-check"
                    aria-label="Save fulfill quantity"
                    disabled={!canSubmitQuantity}
                    onClick={submitQuantity}
                  >
                    <FaCheck />
                  </button>
                </div>
              </div>
            </div>
            {error ? <div className="inventory-transfer-change-error" role="alert">{error}</div> : null}
            <div className="inventory-transfer-history">
              {history.length === 0 ? (
                <div className="inventory-transfer-history-empty">No changes yet</div>
              ) : (
                <ul className="inventory-transfer-history-list">
                  {history.map((event) => (
                    <li key={event.id}>
                      <div className="inventory-transfer-history-name">{event.name}</div>
                      <div className="inventory-transfer-history-change">{event.change}</div>
                      <div className="inventory-transfer-history-meta">
                        {event.actorName} · {formatEventWhen(event.createdAt)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="product-info-notes">
            <button
              type="button"
              className="product-info-note-btn product-info-note-btn-add"
              onClick={() => {
                setPendingSku(null);
                setSearchOpen(true);
              }}
            >
              Substitute SKU
            </button>
            <div className="inventory-transfer-action-divider" role="separator" />
            <button
              type="button"
              className="product-info-note-btn product-info-note-btn-add inventory-transfer-print-labels"
              disabled={printing}
              onClick={() => {
                void onPrintStoreLabels();
              }}
            >
              {printing ? 'Printing…' : 'Print Store Labels'}
            </button>
            {printError ? <div className="inventory-transfer-change-error" role="alert">{printError}</div> : null}
          </div>
        </div>
      </div>
      <ProductNameSearchModal
        open={searchOpen}
        title="Search by SKU or Name"
        matchSku
        showThumbnails
        onClose={() => {
          setSearchOpen(false);
          setPendingSku(null);
        }}
        onSelect={setPendingSku}
        footer={pendingSku ? (
          <div className="inventory-transfer-substitute-confirm">
            <div className="inventory-transfer-substitute-name">{pendingSku.name}</div>
            <div className="inventory-transfer-substitute-sku">{renderSkuWithSoftBreaks(pendingSku.sku)}</div>
            {existingLine ? (
              <p className="inventory-transfer-substitute-merge">
                {`This SKU is already on the transfer request. Click confirm to add ${line.quantity} ${line.quantity === 1 ? 'unit' : 'units'} to ${pendingSku.sku}, and drop ${line.sku} from the transfer.`}
              </p>
            ) : null}
            {error ? <div className="inventory-transfer-change-error" role="alert">{error}</div> : null}
            <div className="product-info-notes inventory-transfer-substitute-note">
              {noteText && !noteEditorOpen ? (
                <div className="product-info-note-text">{noteText}</div>
              ) : null}
              {noteEditorOpen ? (
                <textarea
                  className="product-info-note-input"
                  value={noteInput}
                  onChange={(event) => setNoteInput(event.target.value)}
                  placeholder="Enter note..."
                  rows={3}
                  autoFocus
                />
              ) : null}
              {!noteText && !noteEditorOpen ? (
                <div className="product-info-note-btn-wrapper">
                  <button
                    type="button"
                    className="product-info-note-btn product-info-note-btn-add"
                    onClick={() => {
                      setNoteInput('');
                      setNoteEditorOpen(true);
                    }}
                  >
                    Add Note
                  </button>
                </div>
              ) : null}
              {noteEditorOpen ? (
                <button
                  type="button"
                  className={`product-info-note-btn product-info-note-btn-write ${noteInput.trim() ? 'active' : ''}`}
                  onClick={writeNote}
                  disabled={!noteInput.trim()}
                >
                  Write Note
                </button>
              ) : null}
              {noteText && !noteEditorOpen ? (
                <div className="product-info-note-btn-wrapper">
                  <button
                    type="button"
                    className="product-info-note-btn product-info-note-btn-add"
                    onClick={() => {
                      setNoteInput(noteText);
                      setNoteEditorOpen(true);
                    }}
                  >
                    Edit Note
                  </button>
                </div>
              ) : null}
            </div>
            <button
              type="button"
              className="inventory-transfer-confirm-btn"
              disabled={saving}
              onClick={confirmSubstitute}
            >
              {saving ? 'Saving…' : 'Confirm'}
            </button>
          </div>
        ) : null}
      />
    </div>
  );
}

export default InventoryTransferChangeModal;
